import React, { useRef, useEffect, useState, useCallback } from "react";
import { Stack, Shimmer, ShimmerElementType, MessageBar, MessageBarType, TooltipHost, Icon } from "@fluentui/react";
import { HeatMapChart } from "@fluentui/react-charting";
import { useHealthHeatMapData, HealthViewMode } from "./useCustomerHealthHooks";
import {
  CUSTOMER_HEALTH_STATUS_COLORS,
  getIndicatorTooltipMap,
  resolveIndicatorTooltip
} from "./CustomerHealthView.constants";

const formatIndicatorKey = (key: string): string => {
  if (key === "ChurnRate4Months") return "4 Months Churn Rate";
  return key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
};

interface CustomerHealthHeatMapProps {
  tenantId: string;
  productFamily: string;
  viewMode?: HealthViewMode;
  onCellClick?: (productName: string, indicator: string) => void;
}

interface HeatMapDataItem {
  tenantId: string;
  productFamily: string;
  factDate: string;
  productName: string;
  indicator: string;
  value: number | null;
  scoreRange: string;
}

interface YAxisIconPosition {
  label: string;
  tooltip: string;
  top: number;
  left: number;
}

export const CustomerHealthHeatMap = ({ tenantId, productFamily, viewMode = "tenant", onCellClick }: CustomerHealthHeatMapProps) => {
  const entityLabel = viewMode === "tpid" ? "TPID" : "tenant";
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [iconPositions, setIconPositions] = useState<YAxisIconPosition[]>([]);
  const [chartWidth, setChartWidth] = useState<number>(900);
  const { data: heatMapData = [], isLoading: loading, error } = useHealthHeatMapData(
    tenantId, productFamily, viewMode
  );

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setChartWidth(Math.max(w, 600));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const computeIconPositions = useCallback(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const positions: YAxisIconPosition[] = [];

    const tooltipMap = getIndicatorTooltipMap(productFamily);

    // Build a displayed-label -> raw-indicator-key map from the data so we
    // can resolve tooltips by the same key the indicator cards use.
    const displayedToRaw: Record<string, string> = {};
    for (const item of heatMapData as HeatMapDataItem[]) {
      if (!item.indicator) continue;
      displayedToRaw[formatIndicatorKey(item.indicator)] = item.indicator;
    }

    const resolveTooltip = (displayText: string): string | undefined => {
      const direct = resolveIndicatorTooltip(displayText, tooltipMap);
      if (direct) return direct;
      const viaData = resolveIndicatorTooltip(displayedToRaw[displayText], tooltipMap);
      if (viaData) return viaData;
      for (const [key, tooltip] of Object.entries(tooltipMap)) {
        if (formatIndicatorKey(key) === displayText) return tooltip;
      }
      return undefined;
    };

    // Find y-axis tick text elements
    let yAxisTicks = container.querySelectorAll("g[class*='yAxis'] .tick text, g.yAxis .tick text");
    if (yAxisTicks.length === 0) {
      yAxisTicks = container.querySelectorAll(".tick text");
    }

    yAxisTicks.forEach((textEl) => {
      const svgText = textEl as SVGTextElement;
      const displayText = svgText.textContent?.trim() ?? "";
      const tooltip = resolveTooltip(displayText);
      if (!tooltip) return;

      const textRect = svgText.getBoundingClientRect();
      positions.push({
        label: displayText,
        tooltip,
        top: textRect.top - containerRect.top + textRect.height / 2 - 8,
        left: textRect.right - containerRect.left + 4
      });
    });

    setIconPositions(positions);
  }, [productFamily, heatMapData]);

  useEffect(() => {
    if (!loading && !error && (heatMapData as HeatMapDataItem[]).length > 0) {
      // Delay to allow the chart to finish rendering
      const timer = setTimeout(computeIconPositions, 500);
      return () => clearTimeout(timer);
    }
  }, [heatMapData, loading, error, computeIconPositions, chartWidth]);

  const typedData = (heatMapData as HeatMapDataItem[]);

  const handleChartClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!onCellClick) return;
    const container = chartContainerRef.current;
    if (!container) return;

    // Only handle clicks on SVG rect cells (the heatmap cells)
    const target = e.target as SVGElement;
    if (target.tagName !== "rect") return;

    const rectBBox = target.getBoundingClientRect();
    const rectCenterX = rectBBox.left + rectBBox.width / 2;
    const rectCenterY = rectBBox.top + rectBBox.height / 2;

    // Find the closest x-axis tick label (product name)
    const xAxisTicks = container.querySelectorAll("g[class*='xAxis'] .tick text, g.xAxis .tick text, .tick text");
    let closestProduct = "";
    let minXDist = Infinity;
    xAxisTicks.forEach((textEl) => {
      const rect = textEl.getBoundingClientRect();
      const labelCenterX = rect.left + rect.width / 2;
      const dist = Math.abs(labelCenterX - rectCenterX);
      if (dist < minXDist) {
        minXDist = dist;
        closestProduct = textEl.textContent?.trim() ?? "";
      }
    });

    // Find the closest y-axis tick label (indicator name)
    const yAxisTicks = container.querySelectorAll("g[class*='yAxis'] .tick text, g.yAxis .tick text");
    let closestIndicator = "";
    let minYDist = Infinity;
    yAxisTicks.forEach((textEl) => {
      const rect = textEl.getBoundingClientRect();
      const labelCenterY = rect.top + rect.height / 2;
      const dist = Math.abs(labelCenterY - rectCenterY);
      if (dist < minYDist) {
        minYDist = dist;
        closestIndicator = textEl.textContent?.trim() ?? "";
      }
    });

    if (closestProduct && closestIndicator) {
      onCellClick(closestProduct, closestIndicator);
    }
  }, [onCellClick]);

  if (loading) {
    return (
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { width: 900 } }}>
        {/* Column headers shimmer — offset to match chart left margin */}
        <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { paddingLeft: 200 } }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Shimmer
              key={`col-${i}`}
              shimmerElements={[{ type: ShimmerElementType.line, width: 120, height: 16 }]}
            />
          ))}
        </Stack>
        {/* Row shimmers mimicking heatmap grid */}
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <Stack key={`row-${row}`} horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            {/* Row label — matches chart left margin */}
            <Shimmer
              shimmerElements={[{ type: ShimmerElementType.line, width: 180, height: 40 }]}
              styles={{ root: { flexShrink: 0 } }}
            />
            {/* Cells — fill remaining width equally */}
            {[0, 1, 2, 3, 4].map((col) => (
              <Shimmer
                key={`cell-${row}-${col}`}
                shimmerElements={[{ type: ShimmerElementType.line, width: "100%", height: 40 }]}
                styles={{ root: { flex: 1 } }}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    );
  }

  if (error) {
    return (
      <MessageBar messageBarType={MessageBarType.error}>
        Failed to load heat map data. Please refresh the page or try again later.
      </MessageBar>
    );
  }

  if (typedData.length === 0) {
    return (
      <MessageBar messageBarType={MessageBarType.warning}>
        No heat map data available for this {entityLabel}.
      </MessageBar>
    );
  }

  // Transform data grouped by scoreRange for HeatMapChart

  const getScoreColor = (value: number): string => {
    if (value >= 71) return CUSTOMER_HEALTH_STATUS_COLORS.Healthy;
    if (value >= 41) return CUSTOMER_HEALTH_STATUS_COLORS.Warning;
    return CUSTOMER_HEALTH_STATUS_COLORS.Critical;
  };

  // Group cells by their discrete color instead of by scoreRange
  const colorGroups: Record<string, { x: string; y: string; value: number; rectText: string; color: string }[]> = {
    Healthy: [],
    Warning: [],
    Critical: [],
    "N/A": []
  };

  for (const item of typedData) {
    if (item.value === null || item.value === undefined) {
      colorGroups["N/A"].push({
        x: item.productName,
        y: item.indicator,
        value: -1,
        rectText: "N/A",
        color: CUSTOMER_HEALTH_STATUS_COLORS.NA
      });
    } else {
      const groupKey = item.value >= 71 ? "Healthy" : item.value >= 41 ? "Warning" : "Critical";
      colorGroups[groupKey].push({
        x: item.productName,
        y: item.indicator,
        value: item.value,
        rectText: item.value.toString(),
        color: getScoreColor(item.value)
      });
    }
  }

  const chartData = [
    { legend: "Healthy", value: 100, data: colorGroups.Healthy },
    { legend: "Warning", value: 50, data: colorGroups.Warning },
    { legend: "Critical", value: 20, data: colorGroups.Critical },
    { legend: "N/A", value: -1, data: colorGroups["N/A"] },
  ].filter(item => item.data.length > 0);

  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { width: "100%", minWidth: 0 } }}>
      <div
        className="heatmap-container"
        ref={chartContainerRef}
        style={{
          position: "relative",
          cursor: onCellClick ? "pointer" : undefined,
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "hidden",
        }}
        onClick={handleChartClick}
      >
        <HeatMapChart
          chartTitle="Health Score by Product and Indicator"
          data={chartData}
          width={chartWidth}
          height={500}
          margins={{ left: 230, top: 20, right: 20, bottom: 40 }}
          enableReflow={true}
          hideLegend={true}
          hideTooltip={false}
          yAxisStringFormatter={(indicator) => {
            if (indicator === "ChurnRate4Months") return "4 Months Churn Rate";
            if (
              productFamily?.toLowerCase() === "purview" &&
              /feature.*request.*health|feature_request_health/i.test(indicator ?? "")
            ) {
              return "Feature Health Score";
            }
            return indicator.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
          }}
          legendProps={{ styles: { root: { display: "none" } } }}
          domainValuesForColorScale={[-1, -0.5, 0, 40, 41, 70, 71, 100]}
          rangeValuesForColorScale={[
            CUSTOMER_HEALTH_STATUS_COLORS.NA,
            CUSTOMER_HEALTH_STATUS_COLORS.NA,
            CUSTOMER_HEALTH_STATUS_COLORS.Critical,
            CUSTOMER_HEALTH_STATUS_COLORS.Critical,
            CUSTOMER_HEALTH_STATUS_COLORS.Warning,
            CUSTOMER_HEALTH_STATUS_COLORS.Warning,
            CUSTOMER_HEALTH_STATUS_COLORS.Healthy,
            CUSTOMER_HEALTH_STATUS_COLORS.Healthy
          ]}
        />
        <style>{`
          .heatmap-container .tick text {
            font-size: 14px !important;
            font-weight: 600 !important;
          }
          .heatmap-container g[class*="yAxis"] text {
            transform: translateX(-30px);
          }
        `}</style>
        {iconPositions.map((pos) => (
          <TooltipHost
            key={pos.label}
            content={pos.tooltip}
            styles={{ root: { position: "absolute", top: pos.top, left: pos.left, zIndex: 1 } }}
          >
            <Icon
              iconName="Info"
              aria-label={`Info about ${pos.label}`}
              styles={{
                root: {
                  fontSize: 14,
                  color: "#616161",
                  cursor: "pointer",
                  "&:hover": { color: "#0078d4" }
                }
              }}
            />
          </TooltipHost>
        ))}
      </div>
    </Stack>
  );
};
